/**
 * Exam API Service
 * Handles all exam-related API calls from OpenAPI spec
 */
import apiService from './apiService';
import { api as enhancedApi } from './enhancedApiService';
import { request as apiRequest } from '../lib/http';
import * as examApi from '../utils/examApi';
import { extractAndRegisterLinks, registerLink, getResourceLink } from '../utils/linkRegistry';

class ExamService {
  basePath = '/api/exam';

  /**
   * Get all exam sessions for the current user with optional filters
   * @param {Object} params - Filter parameters
   * @param {number} [params.ExamTypeId] - Filter by exam type ID
   * @param {string} [params.Mode] - Filter by exam mode (e.g., "Practice", "Simulated")
   * @param {string} [params.StartDateFrom] - Filter by start date (from)
   * @param {string} [params.StartDateTo] - Filter by start date (to)
   * @param {boolean} [params.IsCompleted] - Filter by completion status
   * @param {number} [params.PageNumber] - Page number for pagination
   * @param {number} [params.PageSize] - Page size for pagination
   * @returns {Promise<Array>} - Array of exam sessions
   */
  async getExamSessions(params = {}) {
    return enhancedApi.get(this.basePath, params);
  }
  /**
   * Get a specific exam session by ID
   * @param {number} id - The ID of the exam session to retrieve
   * @returns {Promise<Object>} - The exam session details
   */
  async getExamSessionById(id) {
    try {
      // First fetch the basic session data
      const sessionData = await enhancedApi.get(`${this.basePath}/${id}`);
      
      if (!sessionData) {
        console.error(`Failed to fetch session data for ID: ${id}`);
        return null;
      }
      
      // console.log(`Fetched session data for ID ${id}:`, sessionData);
      
      // Import questionService dynamically to avoid circular dependencies
      const { fetchQuestionDetailsForSession } = await import('./questionService/index');
      
      // Now fetch the detailed question data for all questions in the session
      if (typeof fetchQuestionDetailsForSession === 'function') {
        try {
          const detailedQuestions = await fetchQuestionDetailsForSession(sessionData);
          
          // Check if we got any questions back
          if (detailedQuestions && detailedQuestions.length > 0) {
            // console.log(`Fetched ${detailedQuestions.length} detailed questions for session ${id}`);
            
            // Update the session data with the detailed questions
            // We need to preserve the original session structure
            if (sessionData.examQuestions && Array.isArray(sessionData.examQuestions)) {
              sessionData.examQuestions = detailedQuestions;
            } else if (sessionData.questions && Array.isArray(sessionData.questions)) {
              sessionData.questions = detailedQuestions;
            } else {
              // For the format where session ID is a property name
              for (const key in sessionData) {
                if (sessionData[key] && 
                    typeof sessionData[key] === 'object' && 
                    sessionData[key].examQuestions && 
                    Array.isArray(sessionData[key].examQuestions)) {
                  sessionData[key].examQuestions = detailedQuestions;
                  break;
                }
              }
            }
          }
        } catch (err) {
          console.error(`Error fetching detailed questions for session ${id}:`, err);
          // Continue with basic session data if detailed fetching fails
        }
      }
      
      return sessionData;
    } catch (error) {
      console.error(`Error in getExamSessionById for ID ${id}:`, error);
      return null;
    }
  }
  /**
   * Start a new exam session
   * @param {Object} requestData - The session request data
   * @param {number} requestData.examTypeId - The ID of the exam type
   * @param {string} requestData.type - The type of the exam (must be "Practice" or "Simulated")
   * @param {number[]} [requestData.difficulty] - Optional difficulty levels
   * @param {number[]} [requestData.categoryIds] - Optional category IDs
   * @param {boolean} [requestData.hasImage] - Optional filter for questions with images
   * @param {boolean} [requestData.aiGenerated] - Optional filter for AI-generated questions
   * @param {boolean} [requestData.neverSeen] - Optional filter for unseen questions
   * @param {boolean} [requestData.answeredIncorrectly] - Optional filter for incorrectly answered questions
   * @param {boolean} [requestData.answeredCorrectly] - Optional filter for correctly answered questions
   * @param {boolean} [requestData.seenBefore] - Optional filter for previously seen questions
   * @param {string} [requestData.title] - Optional exam title
   * @returns {Promise<Object>} - The created session data
   */
  async startExamSession(requestData) {
    try {
      // console.log('ExamService: Trying enhanced API call for startExamSession');
      // Use the enhanced examApi that handles HATEOAS links
      const result = await examApi.startExamSession(requestData);
      // console.log('ExamService: Enhanced API call succeeded');
      return result;
    } catch (error) {
      // Fall back to the traditional endpoint if the new one fails
      console.warn('ExamService: Enhanced API call failed, falling back to traditional endpoint', error);
      // console.log('ExamService: Enhanced API error status:', error.status);
      // console.log('ExamService: Enhanced API error data:', error.data);
      
      try {
        // console.log('ExamService: Trying fallback API call');
        const fallbackResult = await enhancedApi.post(`${this.basePath}/start`, requestData);
        // console.log('ExamService: Fallback API call succeeded');
        return fallbackResult;
      } catch (fallbackError) {
        console.error('ExamService: Both API calls failed');
        // console.log('ExamService: Fallback error status:', fallbackError.status);
        // console.log('ExamService: Fallback error data:', fallbackError.data);
        // console.log('ExamService: Fallback error message:', fallbackError.message);
        
        // Check for quota error in either the original error or fallback error
        // Look for status 403 OR detail field indicating quota exceeded
        const isOriginalQuotaError = (
          (error.status === 403 && error.data && (error.data.detail || error.data.usageMetrics)) ||
          (error.data && error.data.status === 403 && (error.data.detail || error.data.usageMetrics))
        );
        
        const isFallbackQuotaError = (
          (fallbackError.status === 403 && fallbackError.data && (fallbackError.data.detail || fallbackError.data.usageMetrics)) ||
          (fallbackError.data && fallbackError.data.status === 403 && (fallbackError.data.detail || fallbackError.data.usageMetrics))
        );
        
        if (isOriginalQuotaError) {
          // console.log('ExamService: Throwing original error (looks like quota)');
          throw error;
        } else if (isFallbackQuotaError) {
          // console.log('ExamService: Throwing fallback error (looks like quota)');
          throw fallbackError;
        } else {
          // console.log('ExamService: Throwing fallback error (no quota detected)');
          throw fallbackError;
        }
      }
    }
  }  /**
   * End an active exam session
   * @param {number} id - The ID of the session to end
   * @param {Object} data - Request data
   * @param {number} data.timeSpent - Time spent on the exam in seconds
   * @returns {Promise<Object>} - The updated session data
   */
  async endExamSession(id, data) {
    try {
      // Use the enhanced examApi that handles HATEOAS links
      return await examApi.endExamSession(id, data);
    } catch (error) {
      // Fall back to the traditional endpoint if the new one fails
      console.warn('Enhanced API call failed, falling back to traditional endpoint', error);
      return enhancedApi.put(`${this.basePath}/${id}/end`, data);
    }
  }

  /**
   * Update an active exam session
   * @param {number} id - The ID of the session to update
   * @param {Object} data - Request data
   * @param {number} data.timeSpent - Time spent on the exam in seconds
   * @param {string} [data.Title] - Optional Title
   * @returns {Promise<Object>} - The updated session data
   */
  async updateExamSession(id, data) {
    return enhancedApi.put(`${this.basePath}/${id}/update`, data);
  }

  /**
   * Pause an active exam session
   * @param {number} id - The ID of the session to pause
   * @param {Object} data - Request data
   * @param {number} data.timeSpent - Time spent on the exam in seconds
   * @param {string} [data.Title] - Optional Title
   * @returns {Promise<Object>} - The updated session data
   */
  async pauseExamSession(id, data) {
    return enhancedApi.put(`${this.basePath}/${id}/pause`, data);
  }
  /**
   * Get the count of questions based on filter parameters
   * @param {Object} filterData - Question filter parameters
   * @returns {Promise<number>} - Count of matching questions
   */
  async getQuestionsCount(filterData = {}) {
    try {
      // console.log('[examService] Fetching question count with filter:', filterData);
      const response = await enhancedApi.post(`/api/question/count`, filterData);
      
      // Handle different response formats - the API might return { count: number } or just a number
      const count = typeof response === 'object' && response.count !== undefined 
        ? response.count 
        : (typeof response === 'number' ? response : 0);
      
      // console.log('[examService] Question count response:', count);
      return count;
    } 
    catch (error) {
      console.error('[examService] Error fetching question count:', error);
      throw error;
    }
  }
  /**
   * Submit an answer for a question in the exam
   * @param {number} examId - The exam session ID
   * @param {number} questionId - The question ID
   * @param {Object} answerData - The answer data
   * @param {string} [submitLink] - Optional HATEOAS link for submitting
   * @returns {Promise<Object>} - The response data
   */
  async submitAnswer(examId, questionId, answerData, submitLink) {
    try {
      // Use the enhanced examApi that handles HATEOAS links
      return await examApi.submitAnswer(examId, questionId, answerData, submitLink);
    } catch (error) {
      // Fall back to the traditional endpoint if the new one fails
      console.warn('Enhanced API call failed, falling back to traditional endpoint', error);
      return enhancedApi.post(`${this.basePath}/${examId}/question/${questionId}`, answerData);
    }
  }/**
   * Get questions for an exam session using HATEOAS link
   * @param {string} questionsLink - The link to fetch questions
   * @returns {Promise<Array>} - Array of questions
   */  /**
   * Alias for getExamQuestionsByLink - Added for backward compatibility with code that uses getExamQuestions
   * @param {string} link - The HATEOAS link to fetch questions
   * @returns {Promise<Array>} - Array of questions
   */
  async getExamQuestions(link) {
    // console.log('getExamQuestions called - using getExamQuestionsByLink');
    return this.getExamQuestionsByLink(link);
  }

  async getExamQuestionsByLink(questionsLink) {
    try {
      if (!questionsLink) {
        console.error('No questions link provided');
        return [];
      }
      
      // console.log(`Fetching questions using HATEOAS link: ${questionsLink}`);
      
      // Extract the exam ID from the link if possible (for registering links)
      let examId = null;
      const examIdMatch = questionsLink.match(/\/api\/exam\/(\d+)\/questions/);
      if (examIdMatch && examIdMatch[1]) {
        examId = examIdMatch[1];
      }
        // Ensure the link has a valid format (full URL or API path)
      const response = await apiRequest(questionsLink);
      // console.log('Raw questions response format:', Object.keys(response));
      
      // Case 1: Direct array of questions
      if (Array.isArray(response)) {
        // console.log(`Found ${response.length} questions in direct array response`);
        return response.map(question => {
          // Register the question's links in our registry
          if (question && question.id) {
            extractAndRegisterLinks(question, 'question');
            
            // Link this question to the exam if we have the exam ID
            if (examId) {
              // Register bidirectional relationship
              const submitLink = `/api/exam/${examId}/question/${question.id}`;
              registerLink('exam', examId, `question_${question.id}`, submitLink);
              registerLink('question', question.id, 'exam', `/api/exam/${examId}`);
            }
          }
          // Ensure each question has the necessary HATEOAS links preserved
          if (question && question._links) {
            // console.log('Question has HATEOAS links:', Object.keys(question._links));
          }
          // Add exam ID based links if needed
          this.ensureQuestionLinks(question);
          return question;
        });
      } 
      // Case 2: Object with questions nested
      else if (response && typeof response === 'object') {
        // Check for common wrapper formats
        for (const key of ['items', 'data', 'questions', 'examQuestions', 'results', 'content']) {
          if (Array.isArray(response[key])) {
            // console.log(`Found ${response[key].length} questions in response.${key}`);
            
            // If the response has _links, register those too (collection links)
            if (response._links && examId) {
              extractAndRegisterLinks({ id: examId, _links: response._links }, 'exam');
            }
            
            return response[key].map(q => {
              // Register this question's links
              if (q && q.id) {
                extractAndRegisterLinks(q, 'question');
                
                // Link this question to the exam if we have the exam ID
                if (examId) {
                  const submitLink = `/api/exam/${examId}/question/${q.id}`;
                  registerLink('exam', examId, `question_${q.id}`, submitLink);
                  registerLink('question', q.id, 'exam', `/api/exam/${examId}`);
                }
              }
              return this.ensureQuestionLinks(q);
            });
          }
        }
        
        // Check for an _embedded property (Spring HATEOAS format)
        if (response._embedded && typeof response._embedded === 'object') {
          for (const key in response._embedded) {
            if (Array.isArray(response._embedded[key])) {
              // console.log(`Found ${response._embedded[key].length} questions in _embedded.${key}`);
              
              // If the response has _links, register those too (collection links)
              if (response._links && examId) {
                extractAndRegisterLinks({ id: examId, _links: response._links }, 'exam');
              }
              
              return response._embedded[key].map(q => {
                // Register this question's links
                if (q && q.id) {
                  extractAndRegisterLinks(q, 'question');
                  
                  // Link this question to the exam if we have the exam ID
                  if (examId) {
                    const submitLink = `/api/exam/${examId}/question/${q.id}`;
                    registerLink('exam', examId, `question_${q.id}`, submitLink);
                    registerLink('question', q.id, 'exam', `/api/exam/${examId}`);
                  }
                }
                return this.ensureQuestionLinks(q);
              });
            }
          }
        }
        
        // If the response itself has question properties, wrap it in an array
        if (response.id || response.questionId || response.question || response.body) {
          // console.log('Response appears to be a single question object');
          
          // Register this question's links
          if (response.id) {
            extractAndRegisterLinks(response, 'question');
            
            // Link this question to the exam if we have the exam ID
            if (examId) {
              const submitLink = `/api/exam/${examId}/question/${response.id}`;
              registerLink('exam', examId, `question_${response.id}`, submitLink);
              registerLink('question', response.id, 'exam', `/api/exam/${examId}`);
            }
          }
          
          return [this.ensureQuestionLinks(response)];
        }
      }
      
      console.warn('Unexpected questions format:', response);
      return [];
    } catch (error) {
      console.error('Error fetching questions by link:', error);
      return [];
    }
  }
    /**
   * Ensures a question object has necessary HATEOAS links
   * @private
   * @param {Object} question - The question object
   * @returns {Object} - The question with links
   */
  ensureQuestionLinks(question) {
    if (!question) return question;
    
    const questionId = question.id || question.questionId;
    if (!questionId) return question;
    
    // If the question has no _links object, create one
    if (!question._links) {
      question._links = {};
    }
    
    // If the question has an ID and no answers link, create one
    if (!question._links.answers) {
      // Try to get from registry first, then fallback to default
      const answersLink = getResourceLink('question', questionId, 'answers') || 
                          `/api/question/${questionId}/answers`;
      
      question._links.answers = {
        href: answersLink,
        method: 'GET'
      };
      
      // Also register this link in our registry
      registerLink('question', questionId, 'answers', answersLink);
    }
    
    // Check for content links (for images, attachments, etc.)
    if (question.hasImage && !question._links.image && question.imageUrl) {
      question._links.image = {
        href: question.imageUrl,
        method: 'GET'
      };
      
      // Register this link
      registerLink('question', questionId, 'image', question.imageUrl);
    }
    
    return question;
  }
  /**
   * Get responses for an exam session using HATEOAS link
   * @param {string} responsesLink - The link to fetch responses
   * @returns {Promise<Array>} - Array of user responses
   */
  async getExamResponsesByLink(responsesLink) {
    try {
      if (!responsesLink) {
        console.error('No responses link provided');
        return [];
      }
      
      // console.log(`Fetching responses using HATEOAS link: ${responsesLink}`);
      
      // Extract the exam ID from the link if possible (for registering links)
      let examId = null;
      const examIdMatch = responsesLink.match(/\/api\/exam\/(\d+)\/responses/);
      if (examIdMatch && examIdMatch[1]) {
        examId = examIdMatch[1];
      }
      
      const response = await apiRequest(responsesLink);
      //console.log('Raw responses response format:', Object.keys(response));
      
      // Case 1: Direct array of responses
      if (Array.isArray(response)) {
       // console.log(`Found ${response.length} responses in direct array response`);
        return response.map(resp => {
          // Register response links
          if (resp && resp.id) {
            extractAndRegisterLinks(resp, 'response');
            
            // If we know which exam this belongs to, register that relationship
            if (examId) {
              registerLink('response', resp.id, 'exam', `/api/exam/${examId}`);
              
              // If the response has a questionId, register the question-response relationship
              if (resp.questionId) {
                registerLink('response', resp.id, 'question', `/api/question/${resp.questionId}`);
                registerLink('question', resp.questionId, 'response', `/api/exam/${examId}/responses/${resp.id}`);
              }
            }
          }
          
          // Ensure each response has the necessary HATEOAS links preserved
          if (resp && resp._links) {
           // console.log('Response has HATEOAS links:', Object.keys(resp._links));
          }
          return resp;
        });
      } 
      // Case 2: Object with responses nested
      else if (response && typeof response === 'object') {
        // Check for common wrapper formats
        for (const key of ['items', 'data', 'responses', 'examResponses', 'results', 'content', 'examAnswers', 'answers']) {
          if (Array.isArray(response[key])) {
            // console.log(`Found ${response[key].length} responses in response.${key}`);
            
            // If the response has _links, register those too (collection links)
            if (response._links && examId) {
              extractAndRegisterLinks({ id: examId, _links: response._links }, 'exam');
            }
            
            return response[key].map(resp => {
              // Register response links
              if (resp && resp.id) {
                extractAndRegisterLinks(resp, 'response');
                
                // If we know which exam this belongs to, register that relationship
                if (examId) {
                  registerLink('response', resp.id, 'exam', `/api/exam/${examId}`);
                  
                  // If the response has a questionId, register the question-response relationship
                  if (resp.questionId) {
                    registerLink('response', resp.id, 'question', `/api/question/${resp.questionId}`);
                    registerLink('question', resp.questionId, 'response', `/api/exam/${examId}/responses/${resp.id}`);
                  }
                }
              }
              return resp;
            });
          }
        }
        
        // Check for an _embedded property (Spring HATEOAS format)
        if (response._embedded && typeof response._embedded === 'object') {
          for (const key in response._embedded) {
            if (Array.isArray(response._embedded[key])) {
              // console.log(`Found ${response._embedded[key].length} responses in _embedded.${key}`);
              
              // If the response has _links, register those too (collection links)
              if (response._links && examId) {
                extractAndRegisterLinks({ id: examId, _links: response._links }, 'exam');
              }
              
              return response._embedded[key].map(resp => {
                // Register response links
                if (resp && resp.id) {
                  extractAndRegisterLinks(resp, 'response');
                  
                  // If we know which exam this belongs to, register that relationship
                  if (examId) {
                    registerLink('response', resp.id, 'exam', `/api/exam/${examId}`);
                    
                    // If the response has a questionId, register the question-response relationship
                    if (resp.questionId) {
                      registerLink('response', resp.id, 'question', `/api/question/${resp.questionId}`);
                      registerLink('question', resp.questionId, 'response', `/api/exam/${examId}/responses/${resp.id}`);
                    }
                  }
                }
                return resp;
              });
            }
          }
        }
        
        // If the response itself has response properties, wrap it in an array
        if (response.id || response.questionId || response.isCorrect !== undefined || 
            response.answerId || response.examQuestionId) {
          // console.log('Response appears to be a single response object');
          
          // Register response links
          if (response.id) {
            extractAndRegisterLinks(response, 'response');
            
            // If we know which exam this belongs to, register that relationship
            if (examId) {
              registerLink('response', response.id, 'exam', `/api/exam/${examId}`);
              
              // If the response has a questionId, register the question-response relationship
              if (response.questionId) {
                registerLink('response', response.id, 'question', `/api/question/${response.questionId}`);
                registerLink('question', response.questionId, 'response', `/api/exam/${examId}/responses/${response.id}`);
              }
            }
          }
          
          return [response];
        }
        
        // If the response has nested exam answers, extract them
        if (response.exam && Array.isArray(response.exam.examAnswers)) {
          // console.log(`Found ${response.exam.examAnswers.length} responses in response.exam.examAnswers`);
          
          // If we have an exam object, register its links too
          if (response.exam && response.exam.id) {
            extractAndRegisterLinks(response.exam, 'exam');
            examId = response.exam.id;
          }
          
          return response.exam.examAnswers.map(resp => {
            // Register response links
            if (resp && resp.id) {
              extractAndRegisterLinks(resp, 'response');
              
              // If we know which exam this belongs to, register that relationship
              if (examId) {
                registerLink('response', resp.id, 'exam', `/api/exam/${examId}`);
                
                // If the response has a questionId, register the question-response relationship
                if (resp.questionId) {
                  registerLink('response', resp.id, 'question', `/api/question/${resp.questionId}`);
                  registerLink('question', resp.questionId, 'response', `/api/exam/${examId}/responses/${resp.id}`);
                }
              }
            }
            return resp;
          });
        }
      }
      
      // If no responses were found, return an empty array (common for new sessions)
      if (response && Object.keys(response).length === 0) {
        // console.log('Empty response object - initializing with empty responses array');
        return [];
      }
      
      console.warn('Unexpected responses format:', response);
      return [];
    } catch (error) {
      console.error('Error fetching responses by link:', error);
      return [];
    }
  }
  /**
   * Delete an exam session
   * @param {number} id - The ID of the exam to delete
   * @returns {Promise<Response>} - The response object
   */  async deleteExam(id) {
    try {
      // console.log(`Attempting to delete exam with ID: ${id}`);
      
      const response = await apiRequest(`/api/exam/${id}`, {
        method: 'DELETE'
      });

      // console.log(`Exam ${id} deleted successfully`);
      // Return response-like object with success status and methods for compatibility
      return {
        success: true,
        message: 'Exam deleted successfully',
        ok: true,
        status: 204
      };
    } catch (error) {
      console.error(`Error deleting exam ${id}:`, error);
      // For errors, return response-like object with error status
      return {
        success: false,
        message: error.message || 'Failed to delete exam',
        ok: false,
        status: error.status || 500
      };
    }
  }
}

const examService = new ExamService();
export default examService;
